import React, { createContext, useContext, useEffect, useState, Dispatch } from "react";
import { UserSession } from "../models/UserSession";
import { fetchUserAttributes, FetchUserAttributesOutput } from "aws-amplify/auth";
import { UserTypes } from "../enum/UserTypes";
import { Hub } from "aws-amplify/utils";
import { getStoredActiveSession, clearStoredActiveSession } from "../service/session-service";

interface SessionContextType {
  session: UserSession;
  setSession: Dispatch<React.SetStateAction<UserSession>>;
  refreshSession: () => Promise<void>;
  isLoading: boolean;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

let currentUser: Promise<FetchUserAttributesOutput>;

async function getSession(activeSession: UserSession, setActiveSession: Dispatch<UserSession>, setIsLoading?: Dispatch<React.SetStateAction<boolean>>) {
  try {
    if (setIsLoading) setIsLoading(true);
    
    if (!currentUser) {
      currentUser = fetchUserAttributes();
    }
    let user = await currentUser;
    let user_type = (user["custom:user_type"] as UserTypes) || UserTypes.Unknown;

    if (user_type == UserTypes.Employee) {
      user_type = UserTypes.Client;
    }
    
    // Get stored client/site from localStorage
    const storedSession = getStoredActiveSession();
    
    const session: UserSession = {
      sub: user?.sub || "",
      name: user?.given_name || "",
      email: user.email || "",
      mobile: user.phone_number || "",
      surname: user.family_name || "",
      user_type: user_type,
      active_type: storedSession.active_type || user_type,
      client_id: storedSession.client_id || user["custom:client_id"],
      client_name: storedSession.client_name,
      client_logo_s3_key: storedSession.client_logo_s3_key,
      consultant_id: user["custom:consultant_id"],
      site_id: storedSession.site_id,
      site_name: storedSession.site_name,
      logo_s3_key: storedSession.logo_s3_key,
    };

    if (session.sub !== activeSession.sub) {
      setActiveSession(session);
    }
    
    if (setIsLoading) setIsLoading(false);
  } catch (error) {
    console.error("Error fetching user session:", error);
    console.error(error);
    if (setIsLoading) setIsLoading(false);
  }
}

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState({} as UserSession);
  const [isLoading, setIsLoading] = useState(true);

  const refreshSession = async () => {
    setIsLoading(true);
    currentUser = undefined as any;
    await getSession(session, setSession, setIsLoading);
  };

  const handleAuthStateChange = (event: string) => {
    switch (event) {
      case 'signedIn':
        console.log('User signed in, refreshing session...');
        // Clear cached user data and fetch fresh session
        currentUser = undefined as any;
        getSession({} as UserSession, setSession, setIsLoading);
        break;
      case 'signedOut':
        console.log('User signed out, clearing session...');
        setSession({} as UserSession);
        setIsLoading(false);
        currentUser = undefined as any;
        // Clear stored client/site from localStorage on sign out
        clearStoredActiveSession();
        break;
      case 'tokenRefresh':
        console.log('Token refreshed, updating session...');
        // Optionally refresh session on token refresh
        currentUser = undefined as any;
        getSession({} as UserSession, setSession, setIsLoading);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    // Initialize session on mount
    getSession(session, setSession, setIsLoading);

    // Listen to authentication events
    const hubListener = (data: any) => {
      const { payload } = data;
      handleAuthStateChange(payload.event);
    };

    // Subscribe to authentication events
    const unsubscribe = Hub.listen('auth', hubListener);

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line
  }, []);

  return (
    <SessionContext.Provider value={{ session, setSession, refreshSession, isLoading }}>
      {children}
    </SessionContext.Provider>
  );
};

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
}
